import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, X, Loader2, MapPin, Star, RotateCcw } from "lucide-react";
import apiClient from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PropertyCard from "@/components/property/PropertyCard";
import LocationSelector from "@/components/location/LocationSelector";
import SearchableSelect from "@/components/ui/searchable-select";
import { AmenityIcon, AMENITY_CATEGORY_LABELS } from "@/components/property/amenityMeta";
import { resolveCardPrice } from "@/components/property/roomMeta";
import { photoSrc } from "@/lib/photos";
import { PROPERTY_TYPE_LABELS, GENDER_LABELS } from "@/components/property/PropertyStatusBadge";
import { NEARBY_TYPE_OPTIONS } from "@/components/property/nearbyTypes";

const PROPERTY_TYPES = ["kos", "apartemen", "kontrakan", "coliving", "lainnya"];
const GENDERS = ["putra", "putri", "campur"];
const RATINGS = [3, 3.5, 4, 4.5];
const RADII = [0.5, 1, 2, 5, 10];
const SORTS = [
  { v: "price_asc", l: "Harga Terendah" },
  { v: "price_desc", l: "Harga Tertinggi" },
  { v: "rating", l: "Rating Tertinggi" },
  { v: "distance", l: "Terdekat" },
  { v: "newest", l: "Terbaru" },
];
const LIMIT = 12;
const EMPTY = {
  q: "", location: {}, min_price: "", max_price: "", price_period: "month", property_type: "", gender_type: "",
  amenities: [], room_amenities: [], available_only: false, min_rating: "",
  nearby_type: "", nearby_place_id: "", radius_km: "2", sort: "price_asc",
};
const PRICE_PERIODS = [
  { v: "day", l: "Harian" },
  { v: "month", l: "Bulanan" },
  { v: "year", l: "Tahunan" },
];
const PERIOD_INPUT_LABEL = { day: "Harga Harian (Rp)", month: "Harga Bulanan (Rp)", year: "Harga Tahunan (Rp)" };
const input = "w-full rounded-xl border border-border bg-secondary/40 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors";

function Section({ title, children, testid }) {
  return (
    <div className="pb-4 mb-4 border-b border-border last:border-0" data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{title}</p>
      {children}
    </div>
  );
}

function AmenityChips({ list, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {list.map((a) => {
        const on = selected.includes(a.id);
        return (
          <button key={a.id} type="button" onClick={() => onToggle(a.id)}
            data-testid={`${testid}-${a.slug}`} data-selected={on}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              on ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/40 hover:bg-secondary/70"
            }`}>
            <AmenityIcon name={a.icon} size={12} /> {a.name}
          </button>
        );
      })}
    </div>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [propAmenities, setPropAmenities] = useState([]);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [favIds, setFavIds] = useState(new Set());
  const locRef = useRef(0);

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPage(1); };

  // seed search from URL (?q=<kota>, ?type=<property_type> dari Beranda)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    const type = params.get("type");
    if (q || type) {
      setF((s) => ({ ...s, ...(q ? { q } : {}), ...(type ? { property_type: type } : {}) }));
    }
  }, []);

  // load favorite ids (customer only) for card state
  useEffect(() => {
    if (!user || user.role !== "customer") { setFavIds(new Set()); return; }
    apiClient.get("/favorites/ids")
      .then((r) => setFavIds(new Set(r.data?.property_ids || [])))
      .catch(() => {});
  }, [user]);

  const onToggleFavorite = (propertyId, isFav) => {
    setFavIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.add(propertyId); else next.delete(propertyId);
      return next;
    });
  };

  // load amenity master (split property vs room)
  useEffect(() => {
    apiClient.get("/amenities").then((r) => {
      const all = r.data || [];
      setPropAmenities(all.filter((a) => a.category !== "room"));
      setRoomAmenities(all.filter((a) => a.category === "room"));
    }).catch(() => {});
  }, []);

  // load nearby places when type changes
  useEffect(() => {
    if (!f.nearby_type) { setNearbyPlaces([]); return; }
    apiClient.get("/nearby-places", { params: { type: f.nearby_type } })
      .then((r) => setNearbyPlaces(r.data || [])).catch(() => setNearbyPlaces([]));
  }, [f.nearby_type]);

  const buildParams = useCallback(() => {
    const p = { sort: f.sort, page, limit: LIMIT };
    if (f.q.trim()) p.q = f.q.trim();
    const l = f.location || {};
    if (l.province) p.province_id = l.province;
    if (l.city) p.city_id = l.city;
    if (l.district) p.district_id = l.district;
    if (l.subdistrict) p.subdistrict_id = l.subdistrict;
    if (f.property_type) p.property_type = f.property_type;
    if (f.gender_type) p.gender_type = f.gender_type;
    if (f.min_price) p.min_price = f.min_price;
    if (f.max_price) p.max_price = f.max_price;
    p.price_period = f.price_period;
    if (f.amenities.length) p.amenities = f.amenities.join(",");
    if (f.room_amenities.length) p.room_amenities = f.room_amenities.join(",");
    if (f.available_only) p.available_only = true;
    if (f.min_rating) p.min_rating = f.min_rating;
    if (f.nearby_place_id) { p.nearby_place_id = f.nearby_place_id; p.radius_km = f.radius_km; }
    return p;
  }, [f, page]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    apiClient.get("/properties/search", { params: buildParams() })
      .then((r) => { setResults(r.data.data || []); setPagination(r.data.pagination); })
      .catch(() => { setResults([]); setPagination({ page: 1, total: 0, pages: 0 }); setError(true); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const toggle = (key, id) => set(key, f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id]);
  const reset = () => { setF(EMPTY); setPage(1); locRef.current += 1; };

  // Sort change. "Terdekat" (distance) needs a reference place → guide user to the
  // "Tempat Terdekat" filter section (open drawer on mobile, then scroll into view).
  const handleSort = (v) => {
    set("sort", v);
    if (v !== "distance") return;
    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
    if (!isDesktop) setDrawerOpen(true);
    setTimeout(() => {
      const els = document.querySelectorAll("[data-testid='filter-nearby-section']");
      const el = els[els.length - 1];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-accent", "rounded-xl");
        setTimeout(() => el.classList.remove("ring-2", "ring-accent", "rounded-xl"), 1600);
      }
    }, isDesktop ? 60 : 260);
  };

  const cardOf = (x) => {
    const priced = resolveCardPrice(x, f.price_period, f.available_only);
    return {
      type: PROPERTY_TYPE_LABELS[x.property_type] || x.property_type,
      name: x.name,
      location: `${x.location?.district_name || ""}, ${x.location?.city_name || ""}`.replace(/^, /, ""),
      rating: x.rating || 0,
      reviewCount: x.review_count || 0,
      amenities: (x.amenities || []).map((a) => a.name),
      photo: x.cover_photo_id ? photoSrc(x.cover_photo_id) : null,
      price: priced.label,
      priceSuffix: priced.suffix,
    };
  };

  const FilterBody = (
    <div>
      <Section title="Lokasi">
        <LocationSelector key={locRef.current} onChange={(v) => set("location", v)} />
      </Section>
      <Section title="Harga">
        <select className={`${input} mb-2`} data-testid="filter-price-period"
          value={f.price_period} onChange={(e) => set("price_period", e.target.value)}>
          {PRICE_PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mb-1.5">{PERIOD_INPUT_LABEL[f.price_period]}</p>
        <div className="flex items-center gap-2">
          <input className={input} type="number" min="0" placeholder="Min" data-testid="filter-min-price"
            value={f.min_price} onChange={(e) => set("min_price", e.target.value)} />
          <span className="text-muted-foreground">—</span>
          <input className={input} type="number" min="0" placeholder="Max" data-testid="filter-max-price"
            value={f.max_price} onChange={(e) => set("max_price", e.target.value)} />
        </div>
      </Section>
      <Section title="Tipe Properti">
        <select className={input} data-testid="filter-type" value={f.property_type} onChange={(e) => set("property_type", e.target.value)}>
          <option value="">Semua tipe</option>
          {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t] || t}</option>)}
        </select>
      </Section>
      <Section title="Gender">
        <select className={input} data-testid="filter-gender" value={f.gender_type} onChange={(e) => set("gender_type", e.target.value)}>
          <option value="">Semua</option>
          {GENDERS.map((g) => <option key={g} value={g}>{GENDER_LABELS[g] || g}</option>)}
        </select>
      </Section>
      <Section title="Fasilitas Property">
        <AmenityChips list={propAmenities} selected={f.amenities} onToggle={(id) => toggle("amenities", id)} testid="filter-amenity" />
      </Section>
      <Section title="Fasilitas Kamar">
        <AmenityChips list={roomAmenities} selected={f.room_amenities} onToggle={(id) => toggle("room_amenities", id)} testid="filter-room-amenity" />
      </Section>
      <Section title="Ketersediaan">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-available">
          <input type="checkbox" checked={f.available_only} onChange={(e) => set("available_only", e.target.checked)} className="accent-accent w-4 h-4" />
          Hanya kamar tersedia
        </label>
      </Section>
      <Section title="Rating Minimum">
        <div className="flex flex-wrap gap-1.5" data-testid="filter-rating">
          {RATINGS.map((r) => (
            <button key={r} type="button" onClick={() => set("min_rating", f.min_rating === String(r) ? "" : String(r))}
              data-testid={`filter-rating-${r}`} data-selected={f.min_rating === String(r)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                f.min_rating === String(r) ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/40 hover:bg-secondary/70"}`}>
              <Star size={11} /> {r}+
            </button>
          ))}
        </div>
      </Section>
      <Section title="Tempat Terdekat" testid="filter-nearby-section">
        <div className="mb-2">
          <SearchableSelect
            testid="filter-nearby-type"
            value={f.nearby_type}
            onChange={(v) => setF((s) => ({ ...s, nearby_type: v, nearby_place_id: "" }))}
            options={NEARBY_TYPE_OPTIONS}
            emptyOption={{ value: "", label: "Pilih kategori" }}
            placeholder="Pilih kategori"
            searchPlaceholder="Cari kategori..."
          />
        </div>
        <SearchableSelect
          testid="filter-nearby-place"
          value={f.nearby_place_id}
          onChange={(v) => set("nearby_place_id", v)}
          options={nearbyPlaces}
          disabled={!f.nearby_type}
          emptyOption={{ value: "", label: "Semua tempat" }}
          placeholder={f.nearby_type ? "Pilih tempat" : "Pilih kategori dulu"}
          searchPlaceholder="Cari tempat..."
        />
        {f.nearby_place_id && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-1">Radius</p>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-radius">
              {RADII.map((r) => (
                <button key={r} type="button" onClick={() => set("radius_km", String(r))}
                  data-testid={`filter-radius-${r}`} data-selected={f.radius_km === String(r)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    f.radius_km === String(r) ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/40 hover:bg-secondary/70"}`}>
                  {r < 1 ? `${r * 1000} m` : `${r} km`}
                </button>
              ))}
            </div>
          </div>
        )}
      </Section>
      <button onClick={reset} data-testid="filter-reset" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
        <RotateCcw size={14} /> Reset Filter
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="search-page">
      {/* Search bar */}
      <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 mb-6">
        <Search size={18} className="text-muted-foreground ml-2" />
        <input data-testid="search-input" value={f.q} onChange={(e) => set("q", e.target.value)}
          placeholder="Cari nama hunian, kota, kecamatan, atau alamat..."
          className="flex-1 bg-transparent outline-none text-sm py-2" />
        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-filter-button"
          className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold">
          <SlidersHorizontal size={15} /> Filter
        </button>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="bg-card border border-border rounded-2xl p-5 lg:sticky lg:top-24 max-h-[calc(100vh-8rem)] overflow-y-auto" data-testid="filter-sidebar">
            <h3 className="font-heading text-lg font-semibold mb-4">Filter</h3>
            {FilterBody}
          </div>
        </aside>

        {/* Results */}
        <section className="lg:col-span-3">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <p className="text-sm text-muted-foreground" data-testid="search-results-count">
              {loading ? "Memuat..." : `${pagination.total} hunian ditemukan`}
            </p>
            <select className="rounded-full border border-border bg-secondary/40 px-4 py-2 text-sm outline-none" data-testid="sort-select"
              value={f.sort} onChange={(e) => handleSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>
          ) : error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-12 text-center" data-testid="search-error">
              <p className="font-heading text-lg font-semibold text-destructive">Gagal memuat hasil pencarian.</p>
              <p className="mt-2 text-sm text-muted-foreground">Periksa koneksi Anda lalu coba lagi.</p>
              <button onClick={load} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform" data-testid="search-retry-button">
                <RotateCcw size={15} /> Coba Lagi
              </button>
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="search-empty">
              <p className="font-heading text-lg font-semibold">Tidak ditemukan hunian yang sesuai.</p>
              <p className="mt-2 text-sm text-muted-foreground">Coba kurangi filter, perluas radius, atau reset pencarian.</p>
              <button onClick={reset} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 transition-transform" data-testid="empty-reset-button">
                <RotateCcw size={15} /> Reset Filter
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5" data-testid="search-results-grid">
                {results.map((x, i) => (
                  <div key={x.id} onClick={() => { if (!x.locked) navigate(`/property/${x.id}?price_period=${f.price_period}`); }} className={x.locked ? "cursor-not-allowed" : "cursor-pointer"} data-testid={`search-result-${i}`}>
                    <PropertyCard index={i} property={cardOf(x)} propertyId={x.id} favorited={favIds.has(x.id)} onToggleFavorite={onToggleFavorite} compact locked={x.locked} />
                  </div>
                ))}
              </div>
              {pagination.pages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"
                    className="px-4 py-2 rounded-full border border-border text-sm font-semibold disabled:opacity-40 hover:bg-secondary/60 transition-colors">
                    Sebelumnya
                  </button>
                  <span className="text-sm text-muted-foreground" data-testid="page-indicator">Halaman {pagination.page} / {pagination.pages}</span>
                  <button disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next"
                    className="px-4 py-2 rounded-full border border-border text-sm font-semibold disabled:opacity-40 hover:bg-secondary/60 transition-colors">
                    Berikutnya
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="filter-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[88%] max-w-sm bg-background p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Filter</h3>
              <button onClick={() => setDrawerOpen(false)} data-testid="drawer-close"><X size={20} /></button>
            </div>
            {FilterBody}
            <button onClick={() => setDrawerOpen(false)} className="mt-4 w-full px-4 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold" data-testid="drawer-apply">
              Lihat Hasil
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
